<template>
  <div class="barcode-page">
    <Toast />
    <ConfirmDialog />

    <div class="card">
      <div class="card-header">
        <h5 class="card-title">
          <i class="pi pi-barcode mr-2"></i>
          Cetak Barcode
        </h5>
      </div>

      <div class="card-body">
        <DataTable
          :value="warehouse_outbounds"
          :loading="loading.filter"
          :rowHover="true"
          :selection.sync="selectedItems"
          class="p-datatable-sm"
          :paginator="true"
          :rows="10"
          :rowsPerPageOptions="[10, 20, 50]"
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          currentPageReportTemplate="Menampilkan {first} sampai {last} dari {totalRecords} data"
          responsiveLayout="scroll"
          :scrollable="true"
          scrollHeight="400px"
        >
          <template #empty>
            <div class="empty-state">
              <i class="pi pi-search mb-3" style="font-size: 2rem"></i>
              <p>Data tidak tersedia</p>
              <small class="p-text-secondary"
                >Silakan coba dengan filter yang berbeda</small
              >
            </div>
          </template>

          <template #header>
            <div class="filter-container">
              <div class="p-grid p-fluid filter-grid">
                <div class="p-col-12 p-md-6 p-lg-3">
                  <div class="field">
                    <label for="supplier" class="block mb-1">Supplier</label>
                    <AutoComplete
                      id="supplier"
                      v-model="selected_supplier"
                      :suggestions="filtered_suppliers"
                      @complete="searchSupplier($event)"
                      @input="is_error.supplier = false"
                      field="alias"
                      placeholder="Pilih Supplier"
                      :dropdown="true"
                      :multiple="false"
                      class="w-100"
                      :class="{ 'p-invalid': is_error.supplier }"
                      :disabled="loading.filter"
                    />
                    <small class="p-error" v-if="is_error.supplier">{{
                      error_msg.supplier
                    }}</small>
                  </div>
                </div>

                <div class="p-col-12 p-md-6 p-lg-3">
                  <div class="field">
                    <label for="outlet" class="block mb-1">Outlet</label>
                    <AutoComplete
                      id="outlet"
                      v-model="selected_outlet"
                      :suggestions="outlets"
                      @complete="getOutlets($event)"
                      @input="is_error.outlet = false"
                      field="code"
                      placeholder="Pilih Outlet"
                      :dropdown="true"
                      :multiple="false"
                      class="w-100"
                      :class="{ 'p-invalid': is_error.outlet }"
                      :disabled="loading.filter"
                    >
                      <template #item="slotProps">
                        <div class="outlet-item">
                          <span class="outlet-code">{{
                            slotProps.item.code
                          }}</span>
                        </div>
                      </template>
                    </AutoComplete>
                    <small class="p-error" v-if="is_error.outlet">{{
                      error_msg.outlet
                    }}</small>
                  </div>
                </div>

                <div class="p-col-12 p-md-6 p-lg-3">
                  <div class="field">
                    <label for="docNumber" class="block mb-1">Nomor Bon</label>
                    <InputText
                      id="docNumber"
                      v-model="filter.doc_number"
                      placeholder="Nomor Bon"
                      class="w-100"
                      :disabled="loading.filter"
                    />
                  </div>
                </div>

                <div class="p-col-12 p-md-6 p-lg-3">
                  <div class="field">
                    <label for="docDate" class="block mb-1">Tanggal Bon</label>
                    <Calendar
                      id="docDate"
                      v-model="filter.doc_date"
                      dateFormat="yy-mm-dd"
                      :showIcon="true"
                      class="w-100"
                      :disabled="loading.filter"
                    />
                  </div>
                </div>

                <div class="p-col-12 p-md-6 p-lg-3">
                  <div class="field">
                    <label for="purchaseDoc" class="block mb-1"
                      >Bon Pembelian</label
                    >
                    <InputText
                      id="purchaseDoc"
                      v-model="filter.purchase_doc_number"
                      placeholder="No. Bon Pembelian"
                      class="w-100"
                      :disabled="loading.filter"
                    />
                  </div>
                </div>

                <div class="p-col-12 p-md-6 p-lg-3">
                  <div class="field">
                    <label class="block mb-1">&nbsp;</label>
                    <div class="flex align-items-center">
                      <Checkbox
                        v-model="filter.show_all"
                        :binary="true"
                        @change="showAll()"
                        class="mr-2"
                        :disabled="loading.filter"
                      />
                      <label class="mb-0">Tampilkan semua</label>
                    </div>
                  </div>
                </div>

                <div class="p-col-12 p-md-6 p-lg-3">
                  <div class="field">
                    <label class="block mb-1">&nbsp;</label>
                    <div class="flex gap-2">
                      <Button
                        label="Cari Barcode"
                        icon="pi pi-search"
                        @click="getWarehouseOutbounds()"
                        :loading="loading.filter"
                        class="p-button-primary flex-1"
                        :disabled="loading.filter"
                      />
                      <Button
                        icon="pi pi-refresh"
                        @click="confirmReset"
                        class="p-button-secondary"
                        :disabled="loading.filter"
                        v-tooltip.top="'Reset Filter'"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>

          <Column
            field="cat_no"
            header="No"
            headerStyle="width: 2.5em; text-align: center"
            bodyStyle="width: 2.5em; text-align: center"
          >
            <template #body="slotProps">
              {{ warehouse_outbounds.indexOf(slotProps.data) + 1 }}
            </template>
          </Column>

          <Column
            header="Kode Barang"
            headerStyle="width: 8em; text-align: left"
            bodyStyle="width: 8em; text-align: left"
          >
            <template #body="slotProps">
              <span class="font-medium">{{ slotProps.data.item_code }}</span>
            </template>
          </Column>

          <Column
            header="Nama Barang"
            headerStyle="width: 15em; text-align: left"
            bodyStyle="width: 15em; text-align: left"
          >
            <template #body="slotProps">
              <span class="text-truncate">{{ slotProps.data.item_name }}</span>
            </template>
          </Column>

          <Column
            header="Nomor Bon"
            headerStyle="width: 8em; text-align: left"
            bodyStyle="width: 8em; text-align: left"
          >
            <template #body="slotProps">
              <span>{{ slotProps.data.doc_number }}</span>
            </template>
          </Column>

          <Column
            header="Tanggal Bon"
            headerStyle="width: 8em; text-align: center"
            bodyStyle="width: 8em; text-align: center"
          >
            <template #body="slotProps">
              <span>{{ slotProps.data.doc_date }}</span>
            </template>
          </Column>

          <Column
            header="Harga"
            headerStyle="width: 8em; text-align: right"
            bodyStyle="width: 8em; text-align: right"
          >
            <template #body="slotProps">
              <span class="font-medium">{{
                slotProps.data.price | currency
              }}</span>
            </template>
          </Column>

          <Column
            header="Jumlah"
            headerStyle="width: 6em; text-align: right"
            bodyStyle="width: 6em; text-align: right"
          >
            <template #body="slotProps">
              <span class="font-medium">{{ slotProps.data.qty }}</span>
            </template>
          </Column>

          <Column
            selectionMode="multiple"
            headerStyle="width: 2.5em; text-align: center"
            bodyStyle="width: 2.5em; text-align: center"
          />
        </DataTable>

        <div
          class="p-grid p-justify-center p-mt-4"
          v-if="warehouse_outbounds.length > 0 && selectedItems.length > 0"
        >
          <div class="p-col-12 p-md-6 p-lg-3">
            <Button
              label="Cetak Barcode"
              icon="pi pi-print"
              @click="printBarcode()"
              class="p-button-primary w-100"
              :disabled="loading.filter"
            />
          </div>
        </div>
      </div>
    </div>

    <Dialog
      :visible.sync="print_dialog"
      :style="{ width: '450px' }"
      header="Print Barcode"
      :modal="true"
      class="p-fluid"
    >
      <div class="confirmation-content">
        <i class="pi pi-print p-mr-3" style="font-size: 2rem" />
        <span>Silakan klik save untuk mengunduh barcode</span>
      </div>
      <template #footer>
        <Button
          label="Batal"
          icon="pi pi-times"
          class="p-button-text"
          @click="print_dialog = false"
          :disabled="loading_ok"
        />
        <Button
          label="Simpan"
          icon="pi pi-check"
          class="p-button-text"
          @click="saveClicked()"
          :loading="loading_ok"
        />
      </template>
    </Dialog>

    <!-- Reset Confirmation Dialog -->
    <Dialog
      :visible.sync="resetDialog"
      :style="{ width: '450px' }"
      header="Konfirmasi Reset"
      :modal="true"
    >
      <div class="confirmation-content">
        <i class="pi pi-exclamation-triangle p-mr-3" style="font-size: 2rem" />
        <span>Apakah Anda yakin ingin mereset semua filter?</span>
      </div>
      <template #footer>
        <Button
          label="Batal"
          icon="pi pi-times"
          class="p-button-text"
          @click="resetDialog = false"
        />
        <Button
          label="Reset"
          icon="pi pi-refresh"
          class="p-button-secondary"
          @click="resetFilters"
        />
      </template>
    </Dialog>
  </div>
</template>

<script>
import { saveAs } from 'file-saver'
let moment = require('moment')

export default {
  data() {
    return {
      moment: moment,
      outlets: [],
      selected_outlet: '',

      loading: {
        filter: false
      },

      filter: {
        show_all: false,
        doc_number: '',
        doc_date: '',
        barcode_date: '',
        supplier_value: true,
        purchase_doc_number: ''
      },

      print_dialog: false,

      suppliers: [],
      filtered_suppliers: [],
      selected_supplier: '',

      limit: 1000,
      loading_ok: false,

      warehouse_outbounds: [],
      selectedItems: [],
      selectedItems_id: '',
      ids: [],

      barcodes: null,

      is_error: {
        supplier: false,
        outlet: false
      },

      error_msg: {
        supplier: '',
        outlet: ''
      },

      resetDialog: false
    }
  },

  mounted() {
    this.getSuppliers()
  },

  methods: {
    showAll() {
      this.warehouse_outbounds = []
      this.selected_supplier = ''
      this.selected_outlet = ''
      this.filter.barcode_date = ''
      this.filter.doc_number = ''
      this.filter.doc_date = ''
      this.filter.purchase_doc_number = ''

      this.getSuppliers()
    },

    getSuppliers(event) {
      if (this.filter.show_all) {
        let name = ''
        let code = ''
        let page = 1

        if (event) {
          page = event.page + 1
        }

        if (event.query != null && event.query.match(/^[a-zA-Z]+$/)) {
          name = event.query
        } else if (event.query.match(/^[a-zA-Z0-9]+$/)) {
          code = event.query
        }
        this.$http
          .get(
            `${process.env.VUE_APP_API_URL}/suppliers?name=${name}&code=${code}&page=${page}&limit=5`
          )
          .then(response => {
            if (response.status === 200) {
              this.suppliers = response.data.data.suppliers
              let query = event.query
              let autoFiltered = []
              for (const supplier of this.suppliers) {
                if (
                  supplier.name.toLowerCase().indexOf(query.toLowerCase()) ===
                    0 ||
                  supplier.code.toLowerCase().indexOf(query.toLowerCase()) === 0
                ) {
                  autoFiltered.push(supplier)
                }
              }
              this.filtered_suppliers = autoFiltered
            }
          })
      } else {
        this.$http
          .get(
            `${process.env.VUE_APP_API_URL}/dashboard/warehouse-outbounds/suppliers?status=0&mutation_price=false`
          )
          .then(response => {
            if (response.status === 200) {
              this.suppliers = response.data.data.suppliers
            }
          })
      }
    },

    searchSupplier(event) {
      if (this.filter.show_all) {
        this.getSuppliers(event)
      } else {
        this.filtered_suppliers = this.suppliers
          .filter(item => {
            return (
              item.name.toLowerCase().startsWith(event.query.toLowerCase()) ||
              item.code.toLowerCase().startsWith(event.query.toLowerCase())
            )
          })
          .map(item => {
            return {
              code: item.code,
              id: item.id,
              name: item.name,
              alias: `${item.code} - ${item.name}`
            }
          })
      }
      this.filter.supplier_value = false
    },

    async getWarehouseOutbounds() {
      if (!(this.selected_supplier instanceof Object)) {
        this.is_error.supplier = true
        this.error_msg.supplier = 'Silakan pilih supplier'
        return
      }

      this.loading.filter = true

      try {
        let id_supplier =
          this.selected_supplier === '' ? 0 : this.selected_supplier.id
        let selected_outlet =
          this.selected_outlet == null || isNaN(this.selected_outlet.id)
            ? ''
            : parseInt(this.selected_outlet.id)

        // The calendar binds a Date object; the API expects an ISO date
        // (YYYY-MM-DD). Sending the raw Date.toString() breaks the query.
        let doc_date = this.filter.doc_date
          ? moment(this.filter.doc_date).format('YYYY-MM-DD')
          : ''
        let barcode_date = this.filter.barcode_date
          ? moment(this.filter.barcode_date).format('YYYY-MM-DD')
          : ''

        let url = `${process.env.VUE_APP_API_URL}/dashboard/warehouse-outbounds?sup_id=${id_supplier}&doc_number=${this.filter.doc_number}&doc_date=${doc_date}&outlet_id=${selected_outlet}&purchase_doc_number=${this.filter.purchase_doc_number}`

        if (this.filter.show_all) {
          url += `&barcode_date=${barcode_date}&limit=${this.limit}`
        } else {
          url += `&status=0`
        }

        const response = await this.$http.get(url)

        if (response.status === 200) {
          this.warehouse_outbounds = response.data.data.warehouse_outbound
        } else {
          throw new Error('Gagal mengambil data warehouse outbounds')
        }
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: error.message || 'Terjadi kesalahan saat mengambil data',
          life: 3000
        })
      } finally {
        this.loading.filter = false
      }
    },

    getOutlets(event) {
      let name = ''
      let code = ''

      if (event.query != null && event.query.match(/^[a-zA-Z]+$/)) {
        name = event.query
      } else if (event.query.match(/^[a-zA-Z0-9]+$/)) {
        code = event.query
      }
      this.$http
        .get(`${process.env.VUE_APP_API_URL}/outlets?name=${name}&code=${code}`)
        .then(response => {
          if (response.status === 200) {
            this.outlets = response.data.data.outlets.map(outlet => {
              return {
                code: `${outlet.code} - ${outlet.name}`,
                id: outlet.id
              }
            })
          }
        })
    },

    confirmReset() {
      this.resetDialog = true
    },

    resetFilters() {
      this.filter = {
        show_all: false,
        doc_number: '',
        doc_date: '',
        barcode_date: '',
        supplier_value: true,
        purchase_doc_number: ''
      }
      this.selected_supplier = ''
      this.selected_outlet = ''
      this.resetDialog = false
      this.getWarehouseOutbounds()
    },

    async saveClicked() {
      this.loading_ok = true

      try {
        await this.fetchAndSaveBarcodes()
        await this.updateBarcodeStatus()
        this.downloadBarcodes()
        this.print_dialog = false

        this.$toast.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Barcode berhasil diunduh',
          life: 3000
        })
      } catch (error) {
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: error.message || 'Gagal mengunduh barcode',
          life: 3000
        })
      } finally {
        this.loading_ok = false
      }
    },

    async fetchAndSaveBarcodes() {
      try {
        const response = await this.$http.get(
          `${process.env.VUE_APP_API_URL}/dashboard/warehouse-outbounds/barcodes?id=${this.selectedItems_id}`
        )

        if (response.status === 200) {
          this.barcodes = response.data.data.barcodes
        } else {
          throw new Error('Gagal mengambil data barcode')
        }
      } catch (error) {
        throw new Error('Gagal mengambil data barcode')
      }
    },

    async updateBarcodeStatus() {
      try {
        const response = await this.$http.patch(
          `${process.env.VUE_APP_API_URL}/dashboard/warehouse-outbounds/printed-barcode`,
          { ids: this.ids }
        )

        if (response.status !== 200) {
          throw new Error('Gagal mencetak barcode')
        }
      } catch (error) {
        throw new Error('Gagal mencetak barcode')
      }
    },

    downloadBarcodes() {
      let file = new Blob([this.barcodes], {
        type: 'text/plain;charset=utf-8'
      })
      saveAs(file, 'barcodes.txt')
    },

    printBarcode() {
      this.selectedItems_id = ''
      this.ids = []
      this.print_dialog = true
      if (this.selectedItems.length == 1) {
        this.selectedItems_id = this.selectedItems[0].id
        this.ids.push(this.selectedItems[0].id)
      } else {
        for (let i = 0; i < this.selectedItems.length; i++) {
          let item = this.selectedItems[i]
          this.ids.push(item.id)
          this.selectedItems_id += item.id
          if (i != this.selectedItems.length - 1) this.selectedItems_id += ','
        }
      }
      this.getSuppliers()
    }
  }
}
</script>

<style lang="scss" scoped>
.barcode-page {
  .card {
    background: #ffffff;
    border-radius: 6px;
    box-shadow: 0 2px 1px -1px rgba(0, 0, 0, 0.2),
      0 1px 1px 0 rgba(0, 0, 0, 0.14), 0 1px 3px 0 rgba(0, 0, 0, 0.12);

    .card-header {
      padding: 1.5rem;
      border-bottom: 1px solid #dee2e6;

      .card-title {
        margin: 0;
        font-size: 1.5rem;
        font-weight: 600;
        color: #495057;
        display: flex;
        align-items: center;

        i {
          color: #2196f3;
        }
      }
    }

    .card-body {
      padding: 1.5rem;
    }
  }

  .filter-container {
    margin-bottom: 1rem;

    .filter-grid {
      margin: 0 -0.5rem;

      .p-col-12 {
        padding: 0.5rem;
      }
    }
  }

  .field {
    margin-bottom: 0.5rem;

    label {
      font-weight: 500;
      color: #495057;
      font-size: 0.875rem;
    }
  }

  .outlet-item {
    padding: 0.5rem;

    .outlet-code {
      font-style: italic;
      font-size: 0.875rem;
    }
  }

  .text-truncate {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 300px;
    display: inline-block;
  }

  .text-right {
    text-align: right;
  }

  .font-medium {
    font-weight: 500;
  }

  .w-100 {
    width: 100%;
  }

  .empty-state,
  .loading-state {
    text-align: center;
    padding: 2rem;
    color: #6c757d;

    i {
      color: #2196f3;
      margin-bottom: 1rem;
    }

    p {
      margin-bottom: 0.5rem;
      font-size: 1rem;
    }

    small {
      font-size: 0.875rem;
    }
  }

  .confirmation-content {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;

    i {
      color: #2196f3;
      margin-right: 1rem;
    }
  }

  :deep(.p-datatable) {
    .p-datatable-thead > tr > th {
      background-color: #f8f9fa;
      padding: 0.75rem;
      font-size: 0.875rem;
      font-weight: 600;
      color: #495057;
    }

    .p-datatable-tbody > tr > td {
      padding: 0.75rem;
      font-size: 0.875rem;
    }

    .p-datatable-tbody > tr:hover {
      background-color: rgba(0, 0, 0, 0.02);
    }

    .p-datatable-tbody > tr.p-highlight {
      background-color: rgba(33, 150, 243, 0.1);
    }
  }

  .gap-2 {
    gap: 0.5rem;
  }

  .flex {
    display: flex;
  }

  .flex-1 {
    flex: 1;
  }
}
</style>
